import React, { useEffect, useRef, useState } from 'react';
import proService from '../../services/proService';

/**
 * The optional cover video — in Pro setup (the bordered `hard` look) and inside the Pro page's
 * cover editor (`soft`: the COVER VIDEO · OPTIONAL block of the Portal vocabulary).
 *
 * Two ways in: upload a file, or paste a YouTube/Vimeo link. Uploads encode on
 * Cloudflare's side, so the field polls until it's ready rather than pretending
 * the video is live the moment the transfer finishes.
 *
 * The first photo stays the cover image either way — it's the poster the video
 * fades in over, and the whole header falls back to it if the video can't play.
 *
 * `pageId` pins every call to one page (the editor on /pro/:slug edits that page whatever
 * persona is active); `onChange(video)` lets the page swap the live cover as it changes.
 */
const POLL_MS = 4000;

/** The Pro page's cover-video endpoints, pinned to one page. A club passes its own (clubCoverApi). */
export const proCoverVideoApi = (pageId = null) => ({
  get: () => proService.getCoverVideo(pageId),
  startUpload: () => proService.startCoverVideoUpload(pageId),
  uploadFile: (url, file) => proService.uploadCoverVideoFile(url, file),
  setEmbed: (url) => proService.setCoverVideoEmbed(url, pageId),
  clear: () => proService.clearCoverVideo(pageId),
});

export function statusLine(video, pageWord = 'page') {
  if (!video) return null;
  if (video.status === 'pending') return 'Processing — this usually takes a minute. You can keep working.';
  if (video.status === 'error') return 'That video couldn’t be processed. Try another file.';
  if (video.provider === 'upload') return `Uploaded video — playing on your ${pageWord}.`;
  return `${video.provider === 'youtube' ? 'YouTube' : 'Vimeo'} link — playing on your ${pageWord}.`;
}

export default function CoverVideoField({ soft = false, pageId = null, initial, onChange, api: apiProp = null, pageWord = 'page' }) {
  const api = apiProp || proCoverVideoApi(pageId);
  const [video, setVideoState] = useState(initial === undefined ? null : initial);
  const [loading, setLoading] = useState(initial === undefined);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const fileRef = useRef(null);
  const setVideo = (v) => { setVideoState(v); onChange?.(v); };

  useEffect(() => {
    if (initial !== undefined) return undefined;
    let cancelled = false;
    (async () => {
      const r = await api.get();
      if (!cancelled) { if (r.success) setVideoState(r.data || null); setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // While Cloudflare encodes, keep asking. Stops the moment it's ready or failed.
  useEffect(() => {
    if (!video || video.status !== 'pending') return undefined;
    const t = setInterval(async () => {
      const r = await api.get();
      if (r.success) setVideo(r.data || null);
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, pageId]);

  const pickFile = async (file) => {
    if (!file) return;
    setError(''); setBusy('upload');
    const start = await api.startUpload();
    if (!start.success) { setError(start.error || 'Could not start the upload.'); setBusy(''); return; }

    const up = await api.uploadFile(start.data.uploadUrl, file);
    setBusy('');
    if (!up.success) { setError(up.error || 'Upload failed.'); return; }
    // Transferred, not yet playable — the poll above takes it from here.
    setVideo(start.data.video);
  };

  const saveLink = async () => {
    setError(''); setBusy('link');
    const r = await api.setEmbed(link.trim());
    setBusy('');
    if (!r.success) { setError(r.error || 'That link didn’t work.'); return; }
    setVideo(r.data);
    setLink('');
  };

  const remove = async () => {
    setBusy('remove');
    const r = await api.clear();
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not remove it.'); return; }
    setVideo(null);
  };

  if (soft) {
    const pill = video ? (video.status === 'ready' ? ['LIVE', '#000', '#fff'] : video.status === 'error' ? ['FAILED', '#DC2626', '#fff'] : ['PROCESSING', '#FACC15', '#000']) : null;
    const lineColor = video?.status === 'error' ? '#DC2626' : video?.status === 'pending' ? '#6B7280' : '#000';
    const softPill = (on) => ({ height: on ? 40 : 36, padding: '0 16px', border: 'none', borderRadius: 999, background: on ? '#000' : '#fff', color: on ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: on ? 800 : 700, letterSpacing: '0.14em', cursor: 'pointer', textTransform: 'uppercase' });
    return (
      <div className="flex flex-col font-mono" style={{ gap: 10 }} data-testid="cover-video-field">
        <span className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em' }}>COVER VIDEO · OPTIONAL</span>
          {pill && <span className="rounded-full" style={{ padding: '5px 10px', background: pill[1], color: pill[2], fontSize: 9, fontWeight: 800, letterSpacing: '0.14em' }} data-testid="cover-video-status">{pill[0]}</span>}
        </span>
        <span style={{ fontSize: 11, lineHeight: 1.6, color: '#6B7280' }}>Plays muted and looping in photo 1’s spot, whichever layout you pick. Photo 1 shows first as the poster, then the video fades in over it.</span>
        {loading ? <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: '#9CA3AF' }}>LOADING…</span> : video ? (
          <div className="flex flex-wrap items-center" style={{ gap: 10, padding: '10px 10px 10px 16px', borderRadius: 18, background: '#F5F5F5' }}>
            <span style={{ flex: '1 1 220px', minWidth: 0, fontSize: 12, lineHeight: 1.5, color: lineColor }}>{statusLine(video, pageWord)}</span>
            <button type="button" onClick={remove} disabled={busy === 'remove'} className="transition-colors hover:bg-black hover:text-white disabled:opacity-50" style={softPill(false)}>{busy === 'remove' ? '…' : 'REMOVE VIDEO'}</button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
              <input ref={fileRef} type="file" accept="video/*" hidden onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }} data-testid="cover-video-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy === 'upload'} className="transition-colors hover:bg-[#FF00AA] disabled:opacity-50" style={softPill(true)}>{busy === 'upload' ? 'UPLOADING…' : 'UPLOAD A VIDEO'}</button>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', color: '#9CA3AF' }}>OR</span>
              <span className="flex items-center" style={{ flex: '1 1 260px', minWidth: 0, gap: 4, height: 40, padding: '0 4px 0 16px', borderRadius: 999, background: '#F5F5F5', boxSizing: 'border-box' }}>
                <input type="url" value={link} onChange={(e) => { setLink(e.target.value); setError(''); }} placeholder="YouTube or Vimeo URL" aria-label="YouTube or Vimeo URL" className="min-w-0 flex-1 bg-transparent outline-none" style={{ border: 'none', fontFamily: 'inherit', fontSize: 12, color: '#000' }} />
                <button type="button" onClick={saveLink} disabled={!link.trim() || busy === 'link'} style={{ ...softPill(true), height: 32, padding: '0 14px', fontSize: 9, opacity: link.trim() ? 1 : 0.4 }}>{busy === 'link' ? '…' : 'USE LINK'}</button>
              </span>
            </div>
            <span style={{ fontSize: 11, lineHeight: 1.6, color: '#6B7280' }}>A linked video is cropped to fill the tile, which keeps the site’s own logo out of frame. An uploaded file has no branding at all. Use one if you have it.</span>
          </>
        )}
        {error && <span style={{ fontSize: 12, color: '#DC2626' }}>{error}</span>}
      </div>
    );
  }

  return (
    <div className="border-2 border-black bg-white">
      <div className="border-b-2 border-black px-4 py-3">
        <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black">COVER VIDEO — OPTIONAL</h3>
        <p className="mt-1 font-mono text-xs text-gray-500">
          Plays behind your header, muted and looping. Your first photo is the cover image —
          it shows first, then the video fades in over it.
        </p>
      </div>

      <div className="space-y-4 p-4">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-wider text-gray-500">Loading…</p>
        ) : video ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`border-2 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${
                  video.status === 'ready' ? 'border-black bg-black text-white'
                    : video.status === 'error' ? 'border-red-600 text-red-600'
                      : 'border-yellow-600 text-yellow-700'
                }`}
              >
                {video.status === 'ready' ? 'Live' : video.status === 'error' ? 'Failed' : 'Processing'}
              </span>
              <span className="font-mono text-xs text-gray-600">{statusLine(video, pageWord)}</span>
            </div>
            <button
              type="button"
              onClick={remove}
              disabled={busy === 'remove'}
              className="border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider transition-colors hover:bg-black hover:text-white disabled:opacity-50"
            >
              {busy === 'remove' ? '...' : 'Remove video'}
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy === 'upload'}
                className="border-2 border-black bg-black px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50"
              >
                {busy === 'upload' ? 'Uploading…' : 'Upload a video'}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="h-0.5 flex-1 bg-gray-200" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">or paste a link</span>
              <span className="h-0.5 flex-1 bg-gray-200" />
            </div>

            <div className="flex flex-wrap gap-3">
              <input
                type="url"
                value={link}
                onChange={(e) => { setLink(e.target.value); setError(''); }}
                placeholder="YouTube or Vimeo URL"
                className="min-w-0 flex-1 border-2 border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
              />
              <button
                type="button"
                onClick={saveLink}
                disabled={!link.trim() || busy === 'link'}
                className="border-2 border-black px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider transition-colors hover:bg-black hover:text-white disabled:opacity-50"
              >
                {busy === 'link' ? '...' : 'Use link'}
              </button>
            </div>
            <p className="font-mono text-[11px] text-gray-500">
              A linked video is cropped to fill the header, which keeps the site’s own logo
              out of frame. An uploaded file has no branding at all — use one if you have it.
            </p>
          </>
        )}

        {error && <p className="font-mono text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
}
